import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, FileText, CalendarCheck, User, Building2, MapPin, Handshake } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const TYPE_ICON = {
  enquiry: FileText,
  booking: CalendarCheck,
  guest: User,
  organisation: Building2,
  property: MapPin,
  supplier: Handshake,
};

export function GlobalSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();
  const { primaryWorkspaceType } = useAuth();

  useEffect(() => {
    function onClickOutside(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    if (!query.trim()) {
      setResults(null);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: query } });
        setResults(data);
        setOpen(true);
      } catch {
        setResults(null);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const goTo = (item) => {
    setOpen(false);
    setQuery("");
    const base = primaryWorkspaceType() === "INTERNAL" ? "/control-centre" : "/client";
    if (item.type === "enquiry") navigate(`${base}/enquiries/${item.id}`);
    else if (item.type === "booking") navigate(`${base}/bookings/${item.id}`);
    else if (item.type === "legacy_client") navigate(`${base}/clients/legacy/${item.id}`);
    else if (item.type === "property") navigate(`${base}/properties/${item.id}`);
    else if (item.type === "guest") navigate(`${base}/people/${item.id}`);
  };

  return (
    <div ref={boxRef} className="relative min-w-0 w-full max-w-md">
      <div className="flex items-center gap-2 rounded-lg border border-jigsaw-border bg-jigsaw-bg px-3 py-2 focus-within:ring-2 focus-within:ring-jigsaw-orange/40">
        <Search className="h-4 w-4 text-jigsaw-muted" />
        <input
          data-testid="global-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results && setOpen(true)}
          placeholder="Search by reference, guest, client, property, postcode..."
          className="w-full bg-transparent text-sm text-jigsaw-navy placeholder:text-jigsaw-muted focus:outline-none"
        />
      </div>
      {open && results && (
        <div data-testid="global-search-results" className="absolute z-50 mt-2 w-full rounded-lg border border-jigsaw-border bg-white shadow-lg">
          {loading && <div className="p-3 text-sm text-jigsaw-muted">Searching...</div>}
          {results.exact_match && (
            <button
              onClick={() => goTo(results.exact_match)}
              className="flex w-full items-center justify-between border-b border-jigsaw-border bg-jigsaw-orange/5 px-3 py-2 text-left text-sm hover:bg-jigsaw-orange/10"
            >
              <span className="font-mono-ref font-medium text-jigsaw-navy">{results.exact_match.reference}</span>
              <span className="text-xs uppercase text-jigsaw-orange">Exact match</span>
            </button>
          )}
          {results.results.length === 0 && !results.exact_match && (
            <div className="p-3 text-sm text-jigsaw-muted">No matches found.</div>
          )}
          {results.results.map((item) => {
            const Icon = TYPE_ICON[item.type] || FileText;
            return (
              <button
                key={`${item.type}-${item.id}`}
                onClick={() => goTo(item)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-jigsaw-bg"
              >
                <Icon className="h-4 w-4 text-jigsaw-muted" />
                <span className="font-medium text-jigsaw-navy">{item.label}</span>
                {item.sub_label && <span className="text-xs text-jigsaw-muted">{item.sub_label}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
